import React, { useState } from 'react';
import { Clock, ArrowUpRight, Search } from 'lucide-react';
import useReveal from '../hooks/useReveal';
import { blogPosts, images } from '../mock/mock';
import { useContent } from '../context/ContentContext';

export default function Blog() {
  const { content, txt } = useContent();
  const posts = content.posts && content.posts.length ? content.posts : blogPosts;
  const [query, setQuery] = useState('');
  const filtered = posts.filter(
    (b) =>
      b.title.toLowerCase().includes(query.toLowerCase()) ||
      b.excerpt.toLowerCase().includes(query.toLowerCase()) ||
      (b.tag || '').toLowerCase().includes(query.toLowerCase())
  );
  useReveal([query]);

  const featured = posts[0];

  return (
    <div className="bg-[#E9EBED]">
      <div className="pt-28" />
      <section className="hidden relative pt-44 pb-16 min-h-[480px] flex items-center bg-[#E9E7E2] text-[#2C3A28] overflow-hidden">
        <img
          src={images.blogHeader}
          alt="Handwritten notebook and pen"
          className="absolute inset-0 w-full h-full object-cover object-[center_40%] opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#E9E7E2] via-[#E9E7E2]/70 to-[#E9E7E2]/40" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
          <span className="text-[#2C3A28] text-sm font-semibold uppercase tracking-[0.2em]">
            The journal
          </span>
          <h1 className="mt-4 text-6xl sm:text-7xl max-w-2xl leading-[1.05]" style={{ fontFamily: "'Pinyon Script', cursive" }}>
            Stories from the bench
          </h1>
          <p className="mt-5 max-w-xl font-display text-[#3B2A20]/80 text-lg sm:text-xl leading-relaxed">
            Recipes, rituals and little tales from our kitchen in St Kilda.
          </p>
        </div>
      </section>

      {/* featured */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="reveal group grid lg:grid-cols-2 gap-8 items-center rounded-[2rem] bg-[#EDEAE0] overflow-hidden">
            <div className="h-72 lg:h-[440px] overflow-hidden">
              <img
                src={featured.image}
                alt={featured.title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </div>
            <div className="px-8 sm:px-12 py-10">
              <span className="inline-block rounded-full bg-[#2C3A28] text-white px-4 py-1.5 text-xs font-bold uppercase tracking-wide">
                Featured · {featured.tag}
              </span>
              <h2 className="mt-5 font-display text-4xl font-semibold text-[#2C3A28] leading-tight">
                {featured.title}
              </h2>
              <p className="mt-4 text-[#3B2A20]/75 text-lg leading-relaxed">{featured.excerpt}</p>
              <div className="mt-6 flex items-center gap-4 text-sm text-[#3B2A20]/60">
                <span className="flex items-center gap-1.5">
                  <Clock size={15} /> {featured.read}
                </span>
                <span>{featured.date}</span>
              </div>
              <button className="mt-7 group/btn inline-flex items-center gap-2 font-semibold text-[#2C3A28] hover:text-[#2C3A28]">
                Read the story
                <ArrowUpRight size={18} className="transition-transform group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* grid + search */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <h3 className="font-display text-3xl font-semibold text-[#2C3A28]">{txt('blog', 'allHeading', 'All articles')}</h3>
            <div className="flex items-center gap-2 rounded-full bg-[#EDEAE0] px-5 py-3 w-full sm:w-72">
              <Search size={18} className="text-[#3B2A20]/50" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search stories..."
                className="flex-1 bg-transparent outline-none text-[#2C3A28] placeholder:text-[#3B2A20]/40"
              />
            </div>
          </div>

          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((b, i) => (
              <article
                key={b.id}
                className="reveal group rounded-3xl overflow-hidden bg-[#EDEAE0] hover:-translate-y-1.5 transition-transform duration-400 cursor-pointer"
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <div className="h-56 overflow-hidden">
                  <img
                    src={b.image}
                    alt={b.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <span className="text-xs uppercase tracking-[0.15em] text-[#2C3A28] font-semibold">
                    {b.tag} · {b.read}
                  </span>
                  <h3 className="mt-3 font-display text-2xl font-semibold text-[#2C3A28] leading-snug">
                    {b.title}
                  </h3>
                  <p className="mt-2 text-[#3B2A20]/70 leading-relaxed line-clamp-3">{b.excerpt}</p>
                  <p className="mt-4 text-sm text-[#3B2A20]/50">{b.date}</p>
                </div>
              </article>
            ))}
          </div>
          {filtered.length === 0 && (
            <p className="mt-12 text-center text-[#3B2A20]/60">No stories match “{query}” yet.</p>
          )}
        </div>
      </section>
    </div>
  );
}
